<template>
    <div
        class="col-span-12 flex flex-col relative bg-white shadow-lg top-card-border overflow-hidden ring-1 ring-black ring-opacity-5 rounded-lg mb-5"
        :class="{
            'excluded-card': card.hidden,
            'recommended-card': !card.hidden,
        }"
    >
        <CardExclusion :card="card" :offenders="offenders" v-if="card.hidden" />
        <div
            class="top-card-header px-4 py-1 flex flex-row items-center justify-between w-full"
        >
            <h3 class="text-white font-bold text-lg lg:text-xl">
                {{ user.name }}'s Top Recommended Card
            </h3>
            <span
                class="bg-black/[0.65] text-white rounded-full px-2.5 py-0.5 font-medium text-xs ml-1 hidden md:block"
            >
                <i class="fas fa-bolt text-yellow-400 mr-2"></i>
                Recommended
            </span>
        </div>
        <div
            class="top-card-body flex flex-col xl:flex-row justify-center w-full p-5"
        >
            <div
                class="card-image w-full xl:w-1/3 relative mb-3 flex items-center"
            >
                <a
                    :href="card.application_link"
                    target="_blank"
                    class="w-1/3 xl:w-full"
                >
                    <img
                        :src="`${cdnUrl}/${card.card_image?.path}/_small/${card.card_image?.file_name}`"
                        class="object-contain max-h-24 md:max-h-36 w-auto mx-auto"
                    />
                </a>
                <h3
                    class="text-sm md:text-lg text-gray-800 font-bold relative pl-8 ml-4 !leading-none xl:hidden w-2/3"
                >
                    <span
                        class="text-white text-xs md:mr-2 font-bold bg-orange rounded-full w-5 h-5 text-center inline-flex flex-col leading-none items-center justify-center absolute left-0 top-0"
                    >
                        <span class="relative mt-0">1</span>
                    </span>
                    <a
                        :href="card.application_link"
                        class="hover:text-gray-400 text-base font-semibold !leading-tight"
                        target="_blank"
                    >
                        {{ card.title }}
                    </a>
                </h3>
            </div>
            <div class="xl:pl-7 relative">
                <a
                    v-if="userSettings.is_admin"
                    :href="'/admin/cards/' + card.id + '/edit'"
                    target="blank"
                    class="block absolute top-0 right-0 hover:text-gray-400"
                >
                    <i class="fa-regular fa-pen-to-square"></i>
                </a>
                <div class="card-content-wrapper flex flex-col md:flex-row">
                    <div class="card-info-content md:w-2/3 xl:pr-10">
                        <h3
                            class="text-sm md:text-lg text-gray-800 font-bold mb-4 relative pl-8 !leading-none hidden xl:block"
                        >
                            <span
                                class="text-white text-sm md:mr-2 font-bold bg-orange rounded-full w-6 h-6 text-center inline-flex flex-col leading-none items-center justify-center absolute left-0 top-0"
                            >
                                <span class="relative mt-0">1</span>
                            </span>
                            <a
                                :href="card.application_link"
                                class="hover:text-gray-400"
                                target="_blank"
                            >
                                {{ card.title }}
                            </a>
                        </h3>
                        <div v-if="card.rich_text">
                            <p class="text-xs text-gray-400 mb-1">
                                Current Offer
                            </p>
                        </div>
                        <div v-html="card.rich_text"></div>
                    </div>
                    <div
                        class="card-links w-full md:w-1/3 flex justify-center content-center"
                    >
                        <div
                            class="card-link flex flex-col justify-center lg:mt-8"
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'direct'
                            "
                        >
                            <a
                                :href="card.application_link"
                                class="btn btn-orange !ml-0 !text-md"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Apply Now
                                <i class="fa-regular fa-lock ml-2"></i>
                            </a>

                            <p
                                class="text-center"
                                v-if="card.issuing_bank.link_button_visible"
                            >
                                <small>
                                    on {{ card.issuing_bank.name }}'s secure
                                    site
                                </small>
                            </p>
                        </div>
                        <div
                            class="card-link flex flex-col justify-center content-center lg:mt-8 text-center"
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect'
                            "
                        >
                            <a
                                :href="card.application_link"
                                class="btn btn-orange !ml-0 mb-6 !text-md"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Learn How to Apply
                                <i class="fa-light fa-arrow-right ml-2"></i>
                            </a>
                            <a
                                :href="card.application_link"
                                v-else="card.issuing_bank.link_button_visible"
                                class="text-md font-semibold text-orange hover:text-orange-400 mb-8"
                                target="_blank"
                            >
                                Learn How to Apply
                                <i class="fa-light fa-arrow-right ml-2"></i>
                            </a>
                        </div>
                    </div>
                </div>
                <CouldContributeToFiveTwentyFour
                    :could_contribute_to_five_twenty_four="
                        card.could_contribute_to_five_twenty_four
                    "
                />
                <div class="card-specs flex flex-row justify-between mt-4 pr-8">
                    <p class="text-xs text-gray-400">
                        Annual Fee:
                        <span class="text-base font-bold text-gray-800">
                            ${{ card.annual_fee }}
                        </span>
                        <span
                            v-if="
                                card.issuing_bank &&
                                (card.issuing_bank.name === 'American Express' ||
                                card.issuing_bank.name === 'Citi')
                            "
                            class="ml-2"
                        >
                            <a
                                :href="card.rates_and_fees_link"
                                target="_blank"
                                v-if="
                                    card.issuing_bank &&
                                    (card.issuing_bank.name === 'American Express' ||
                                    card.issuing_bank.name === 'Citi') &&
                                    card.rates_and_fees_link
                                "
                                class="text-orange hover:text-orange-400"
                            >
                                Rates & Fees
                            </a>
                        </span>
                    </p>
                    <p v-if="card.program">
                        <span
                            class="bg-gray-100 text-gray-600 px-3 py-0.5 text-xs font-medium rounded-full ml-1"
                        >
                            {{
                                $filters.titleCase(
                                    goodProgramName(card.program.type)
                                )
                            }}
                            Card
                        </span>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useAppStore } from '@/store/app';
import { useNextCardStore } from '@/store/nextcard';

import CardExclusion from '@/components/next-card/card-exclusion.vue';
import SpecialTag from '@/components/next-card/special-tag.vue';
import CouldContributeToFiveTwentyFour from '@/components/next-card/contribute524Count.vue';

export default {
    components: {
        CardExclusion,
        SpecialTag,
        CouldContributeToFiveTwentyFour,
    },
    props: {
        card: {
            type: Object,
            default: {
                hidden: false,
                image: null,
                title: '',
                rich_text: '',
            },
        },
        user: {
            type: Object,
        },
    },
    computed: {
        ...mapState(useAppStore, ['userSettings']),
        ...mapState(useNextCardStore, ['activeNextCard', 'offenders']),
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    data: () => ({}),
    methods: {
        goodProgramName(type) {
            switch (type) {
                case 'flexible_currency':
                    return 'travel';
                    break;
                default:
                    return type;
            }
        },
    },
    created() {},
    mounted() {},
};
</script>
<style lang="scss" scoped>
.top-card-border {
    border-color: #05578b;
}
.top-card-header {
    background-color: #05578b;
}
</style>
